import React, {
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';

import {
	fetchAccountListingConfig,
} from 'Vulture/Account.js';

import {
	generateConcatenatedColumns,
} from 'Vulture/Content.js';

import {
	updateListingConfigLabel as updateListingConfigLabelRequest,
} from 'Vulture/Listing.js';

import API from 'Vulture/lib/API.js';

import {
	v4 as uuidv4,
} from 'uuid';

export const ListingConfigContext = createContext(null);

export const useListingConfig = () => {
	return useContext(ListingConfigContext);
};

// listing_config columns that hold integration credentials (the rest live in
// setting.* map rows). See applyListingConfigCredentials.
const CREDENTIAL_FIELDS = [
	'provider_auth_code',
	'provider_key',
	'provider_map_identifier',
];

// With <=2 slots (parent counted), split 100 evenly. Otherwise the first
// column takes 40 and the rest split 60. Columns must be sorted by order.
export const reallocateColumnWidths = (columns, hasParent) => {
	const result = [ ...columns, ];
	if (result.length === 0) {
		return result;
	}
	let denominator = result.length;
	if (hasParent) {
		denominator++;
	}
	if (denominator <= 2) {
		for (let i = 0; i < result.length; i++) {
			result[i].width = Math.floor(100 / denominator);
		}
	} else {
		result[0].width = 40;
		denominator--;
		for (let i = 1; i < result.length; i++) {
			result[i].width = Math.floor(60 / denominator);
		}
	}
	return result;
};

// Sets each listing_column.<uuid>'s `order` to its 1-indexed position in
// orderedUUIDs. Sub-keys not in orderedUUIDs are left untouched.
const reorderListingColumns = (configEntry, orderedUUIDs) => {
	if (!configEntry) {
		return configEntry;
	}
	const next = {
		...configEntry,
	};
	orderedUUIDs.forEach((uuid, i) => {
		const key = `listing_column.${uuid}`;
		if (next[key]) {
			next[key] = {
				...next[key],
				order: i + 1,
			};
		}
	});
	return next;
};

// Walks childTiers from rootUUID and returns every UUID reached, root included.
const collectDescendantUUIDs = (listingConfigMap, rootUUID) => {
	if (!listingConfigMap) {
		return [];
	}
	const visited = new Set();
	const stack = [ rootUUID, ];
	while (stack.length > 0) {
		const uuid = stack.pop();
		if (visited.has(uuid)) {
			continue;
		}
		visited.add(uuid);
		const entry = listingConfigMap[uuid];
		const childTiers = Array.isArray(entry?.childTiers) ? entry.childTiers : [];
		for (const childUUID of childTiers) {
			if (!visited.has(childUUID)) {
				stack.push(childUUID);
			}
		}
	}
	return Array.from(visited);
};

// Patch keys that live on listing_config_to_listing_column_map instead of
// the listing_column entity.
const COLUMN_REL_FIELDS = new Set([ 'order', 'width', 'column_metadata', ]);

// One-shot alphabetical sort of root tier UUIDs, used only when a fresh
// listing_config map arrives from the server. Unnamed roots get a
// "List N" fallback numbered in fetch iteration order — same label the
// UI shows — and ties break on UUID for determinism.
export const sortRootsAlphabetically = (listingConfigMap) => {
	if (!listingConfigMap || typeof listingConfigMap !== 'object') {
		return [];
	}
	let unnamedCounter = 1;
	const roots = [];
	for (const [ uuid, entry, ] of Object.entries(listingConfigMap)) {
		if (!entry || !entry.root) {
			continue;
		}
		const rawName = typeof entry.display_name === 'string' ? entry.display_name.trim() : '';
		const displayName = rawName || `List ${unnamedCounter++}`;
		roots.push({ uuid, displayName, });
	}
	roots.sort((a, b) => {
		const nameCmp = a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase());
		if (nameCmp !== 0) {
			return nameCmp;
		}
		return a.uuid.localeCompare(b.uuid);
	});
	return roots.map((r) => {
		return r.uuid;
	});
};

const buildInitialListingConfigSettings = () => {
	return {
		childTiers:          [],
		content_group_count: 1,
		data_width:          80,
		display_name:        '',
		label:               '',
		listing:             true,
		mobile_button_order: 0,
		mobile_click_event:  0,
		mobile_dialing:      false,
		mobile_display:      false,
		mobile_pdf:          false,
		mobile_wayfinding:   false,
		renderName:          'ListingsGroup',
	};
};

// Owns listingConfigMap. Consumers read via useListingConfig() or
// `static contextType = ListingConfigContext`; App.js writes via the ref.
// `app` is the DashboardView; `accountUUID` is the currently-active
// account — captured here so mutation helpers don't need to take it as a
// per-call parameter. Mutation helpers queue through
// app.queuePendingPromiseGroup so changes flush as one group on Save.
export const ListingConfigProvider = forwardRef(function ListingConfigProvider(
	{
		app,
		accountUUID,
		children,
		mode,
	},
	ref
) {
	// Map stays an object even before the first fetch — consumers index into
	// it directly and the previous fd-prop default was {}, not null. The
	// `loaded` flag is the explicit "have we fetched yet" signal for UIs
	// that need a loading state (e.g. SystemProgramming).
	const [ listingConfigMap, setListingConfigMap, ] = useState({});
	const [ listingConfigMapLoaded, setListingConfigMapLoaded, ] = useState(false);

	// Ordered UUIDs of root tiers as rendered in the sidebar. Sorted
	// alphabetically once per fetch (see sortRootsAlphabetically); between
	// fetches we only append on create and splice on delete. Renames don't
	// touch this list, so a pending rename never reorders the sidebar —
	// the next fetch (Save, account switch) is what re-sorts.
	const [ rootOrder, setRootOrder, ] = useState([]);

	// listing_config UUIDs whose column_metadata (concatenator pattern) was
	// touched via updateListingColumn since the last save. Flushed by
	// onListingConfigSettled AFTER the queued metadata thunks have landed
	// server-side, so the regen sees fresh column_metadata. A pendingChanges-
	// only trigger would miss column_metadata entirely (it goes through the
	// promise group, not pendingChanges) and would race the group's flush.
	const pendingConcatenatorRegenRef = useRef(new Set());

	const fetchListingConfigs = useCallback(async () => {
		const data = await fetchAccountListingConfig(accountUUID);
		setListingConfigMap(data || {});
		setRootOrder(sortRootsAlphabetically(data));
		setListingConfigMapLoaded(true);
	}, [ accountUUID, ]);

	// Shared onSettled for the 'listingConfig' promise group — runs once
	// after every queued thunk for this batch has resolved (or rejected).
	// Drain concatenator regens first (server now has fresh column_metadata),
	// then reconcile local state with server truth.
	const onListingConfigSettled = useCallback(async () => {
		const regenUUIDs = Array.from(pendingConcatenatorRegenRef.current);
		pendingConcatenatorRegenRef.current.clear();
		if (regenUUIDs.length > 0) {
			await Promise.all(regenUUIDs.map((uuid) => {
				return generateConcatenatedColumns(uuid);
			}));
		}
		await fetchListingConfigs();
	}, [ fetchListingConfigs, ]);

	const clearListingConfigMap = useCallback(() => {
		setListingConfigMap({});
		setRootOrder([]);
		setListingConfigMapLoaded(false);
	}, []);

	// Reconciles one config's credentials from a server flat-data response
	// (`listing_config.<uuid>` → entry) so the gates that read them — notably
	// the Crawl button's hasSetCredentials — flip without a page reload.
	// `credentialSettingUUIDs` names the setting rels to take; the provider_*
	// columns come along for the providers that store credentials there.
	// Anything else in the response is ignored: the FD nests a thinner
	// listing_column shape than the map holds (order/width, no
	// listing_column_type), and a wider merge could revert an optimistic edit
	// whose write is still queued.
	const applyListingConfigCredentials = useCallback((listingConfigUUID, fd, credentialSettingUUIDs = []) => {
		const fdEntry = fd?.[`listing_config.${listingConfigUUID}`];
		if (!fdEntry) {
			return;
		}
		const credentialKeys = new Set([
			...credentialSettingUUIDs.filter(Boolean).map((settingUUID) => {
				return `setting.${settingUUID}`;
			}),
			...CREDENTIAL_FIELDS,
		]);
		setListingConfigMap((prev) => {
			const entry = prev?.[listingConfigUUID];
			if (!entry) {
				return prev;
			}
			const next = {
				...entry,
			};
			for (const [ key, value, ] of Object.entries(fdEntry)) {
				if (credentialKeys.has(key)) {
					next[key] = value;
				}
			}
			return {
				...prev,
				[listingConfigUUID]: next,
			};
		});
	}, []);

	// listing_config data is keyed solely on accountUUID
	// (fetchAccountListingConfig takes nothing else), so fetch it reactively
	// whenever the active account changes instead of relying on App to poke the
	// ref imperatively. App renders a bare LoadingView — with no provider —
	// until permissions initialize, so an imperative fetchListingConfigs() made
	// while the provider is unmounted hits a null ref and is silently dropped by
	// the `?.` no-op. That race is why listing configs intermittently failed to
	// load on first paint. Keying the fetch to the prop closes it: the effect
	// fires as soon as the provider mounts with an accountUUID, and again on
	// every account switch. Clear-before-fetch avoids rendering the new account
	// against the previous account's tiers while the fetch is in flight.
	useEffect(() => {
		clearListingConfigMap();
		if (accountUUID) {
			fetchListingConfigs();
		}
	}, [ accountUUID, fetchListingConfigs, clearListingConfigMap, ]);

	useEffect(() => {
		if (mode === 'account_settings') {
			clearListingConfigMap();
			fetchListingConfigs();
		}
	}, [ mode, fetchListingConfigs, clearListingConfigMap, ]);

	// Label = the order=1 column. Promotes listingColumnUUID to order 1 and
	// shifts every other column up by one (preserving their relative order).
	// PUT /v1/listing_config/<uuid>/label/<col> performs the same reorder
	// server-side.
	const updateListingConfigLabel = useCallback((listingConfigUUID, listingColumnUUID) => {
		setListingConfigMap((prev) => {
			const entry = prev?.[listingConfigUUID];
			if (!entry || !entry[`listing_column.${listingColumnUUID}`]) {
				return prev;
			}
			const others = Object.keys(entry)
				.filter((m2Key) => {
					return m2Key.startsWith('listing_column.') && m2Key !== `listing_column.${listingColumnUUID}`;
				})
				.sort((a, b) => {
					return (entry[a].order ?? 0) - (entry[b].order ?? 0);
				})
				.map((m2Key) => {
					return m2Key.split('.')[1];
				});
			return {
				...prev,
				[listingConfigUUID]: reorderListingColumns(entry, [ listingColumnUUID, ...others, ]),
			};
		});
		app.queuePendingPromiseGroup('listingConfig', `updateListingConfigLabel_${listingConfigUUID}`, async () => {
			await updateListingConfigLabelRequest(listingConfigUUID, listingColumnUUID);
		}, onListingConfigSettled);
	}, [ app, onListingConfigSettled, ]);

	// Local state only — callers own the server side: deleteListingConfigTier
	// enqueues the DELETE, while the integration-delete flow has already
	// deleted through its own endpoint and just needs the tier off the screen.
	const dropListingConfigSubtreeLocally = useCallback((listingConfigUUID) => {
		const subtreeUUIDs = collectDescendantUUIDs(listingConfigMap || {}, listingConfigUUID);

		setListingConfigMap((prev) => {
			if (!prev) {
				return prev;
			}
			const next = {
				...prev,
			};
			for (const uuid of subtreeUUIDs) {
				delete next[uuid];
			}
			const parentEntry = Object.entries(prev).find((entry) => {
				return entry[1]?.childTiers?.includes(listingConfigUUID);
			});
			if (parentEntry) {
				next[parentEntry[0]] = {
					...next[parentEntry[0]],
					childTiers: (next[parentEntry[0]].childTiers || []).filter((u) => {
						return u !== listingConfigUUID;
					}),
				};
			}
			return next;
		});

		// Splice out if this tier was a root — no-op otherwise. Non-roots
		// aren't in rootOrder, so filter is safe either way.
		setRootOrder((prev) => {
			return prev.filter((u) => {
				return u !== listingConfigUUID;
			});
		});

		// Drop any queued tree-create thunks whose root is in the deleted
		// subtree. The cancel is a no-op for non-root tiers — those got
		// created inside their tree's single thunk and we can't undo from
		// outside it; the caller's DELETE covers them.
		app.cancelPendingPromiseGroupKeys('listingConfig', subtreeUUIDs.map((u) => {
			return `createListingConfigTree_${u}`;
		}));

		return subtreeUUIDs;
	}, [ app, listingConfigMap, ]);

	// Optimistically drops the subtree locally and enqueues a single
	// DELETE /v1/listing_configs/<uuid>; bronco's custom handler cascades
	// soft-deletes through descendants and refuses if any have listings.
	// Pending tier root: the local drop cancels the queued tree-create and we
	// skip the DELETE (server never learned about it).
	const deleteListingConfigTier = useCallback((listingConfigUUID) => {
		const rootWasPending = !!(listingConfigMap || {})[listingConfigUUID]?.pending;

		dropListingConfigSubtreeLocally(listingConfigUUID);

		if (rootWasPending) {
			return;
		}

		app.queuePendingPromiseGroup('listingConfig', `deleteListingConfigTier_${listingConfigUUID}`, async () => {
			await API.deleteRecord('listing_config', listingConfigUUID);
		}, onListingConfigSettled);
	}, [ app, listingConfigMap, dropListingConfigSubtreeLocally, onListingConfigSettled, ]);

	// Patch keys split into listing_config entity fields and 'setting.<uuid>'
	// rels (whose value is { string, bool, ... }). Map-update is server-side
	// upsert (api_map.go), so no client-side existence branch.
	const updateListingConfig = useCallback((listingConfigUUID, patch) => {
		setListingConfigMap((prev) => {
			const next = {
				...(prev || {}),
			};
			next[listingConfigUUID] = {
				...next[listingConfigUUID],
				...patch,
			};
			return next;
		});

		const entityFields = {};
		const settingRels = [];
		for (const [ key, value, ] of Object.entries(patch)) {
			if (key.startsWith('setting.')) {
				settingRels.push({ settingUUID: key.split('.')[1], valueObj: value, });
			} else {
				entityFields[key] = value;
			}
		}

		if (Object.keys(entityFields).length > 0) {
			const key = `updateListingConfig_${listingConfigUUID}_entity_${Object.keys(entityFields).sort().join('_')}`;
			app.queuePendingPromiseGroup('listingConfig', key, async () => {
				await API.updateRecord('listing_config', listingConfigUUID, entityFields);
			}, onListingConfigSettled);
		}
		for (const { settingUUID, valueObj, } of settingRels) {
			app.queuePendingPromiseGroup('listingConfig', `updateListingConfig_${listingConfigUUID}_setting_${settingUUID}`, async () => {
				await API.updateMapRecord('listing_config', listingConfigUUID, 'setting', settingUUID, valueObj);
			}, onListingConfigSettled);
		}
	}, [ app, onListingConfigSettled, ]);

	// Patch keys split into listing_column entity fields and rel fields
	// (COLUMN_REL_FIELDS, which live on listing_config_to_listing_column_map).
	const updateListingColumn = useCallback((listingConfigUUID, listingColumnUUID, patch) => {
		setListingConfigMap((prev) => {
			const next = {
				...(prev || {}),
			};
			const entry = {
				...(next[listingConfigUUID] || {}),
			};
			entry[`listing_column.${listingColumnUUID}`] = {
				...entry[`listing_column.${listingColumnUUID}`],
				...patch,
			};
			next[listingConfigUUID] = entry;
			return next;
		});

		const entityPatch = {};
		const relPatch = {};
		for (const [ key, value, ] of Object.entries(patch)) {
			if (COLUMN_REL_FIELDS.has(key)) {
				relPatch[key] = value;
			} else {
				entityPatch[key] = value;
			}
		}

		// column_metadata is the concatenator pattern — changing it means the
		// derived concatenator values on every listing row for this tier are
		// stale and need regen. onListingConfigSettled drains this set after
		// the queued metadata thunks have landed.
		if ('column_metadata' in relPatch) {
			pendingConcatenatorRegenRef.current.add(listingConfigUUID);
		}

		if (Object.keys(entityPatch).length > 0) {
			const key = `updateListingColumn_${listingColumnUUID}_entity_${Object.keys(entityPatch).sort().join('_')}`;
			app.queuePendingPromiseGroup('listingConfig', key, async () => {
				await API.updateRecord('listing_column', listingColumnUUID, entityPatch);
			}, onListingConfigSettled);
		}
		if (Object.keys(relPatch).length > 0) {
			const key = `updateListingColumn_${listingConfigUUID}_${listingColumnUUID}_rel_${Object.keys(relPatch).sort().join('_')}`;
			app.queuePendingPromiseGroup('listingConfig', key, async () => {
				await API.updateMapRecord('listing_config', listingConfigUUID, 'listing_column', listingColumnUUID, relPatch);
			}, onListingConfigSettled);
		}
	}, [ app, onListingConfigSettled, ]);

	// Swaps the column's listing_column_type rel from oldTypeUUID to
	// newTypeUUID. Server keys the canonical sub-entry by type *name*
	// (e.g. "listing_column_type.string"); the optimistic insert uses a
	// UUID-keyed placeholder until the trailing refetch overwrites it.
	const updateListingColumnType = useCallback((listingConfigUUID, listingColumnUUID, oldTypeUUID, newTypeUUID) => {
		if (oldTypeUUID === newTypeUUID) {
			return;
		}
		setListingConfigMap((prev) => {
			const next = {
				...(prev || {}),
			};
			const entry = {
				...(next[listingConfigUUID] || {}),
			};
			const columnEntry = {
				...(entry[`listing_column.${listingColumnUUID}`] || {}),
			};
			if (oldTypeUUID) {
				for (const [ subKey, subVal, ] of Object.entries(columnEntry)) {
					if (subKey.startsWith('listing_column_type.') && subVal?.uuid === oldTypeUUID) {
						delete columnEntry[subKey];
						break;
					}
				}
			}
			columnEntry[`listing_column_type.${newTypeUUID}`] = {
				uuid: newTypeUUID,
			};
			entry[`listing_column.${listingColumnUUID}`] = columnEntry;
			next[listingConfigUUID] = entry;
			return next;
		});
		app.queuePendingPromiseGroup('listingConfig', `updateListingColumnType_${listingColumnUUID}`, async () => {
			// Single create call: the server-side create_map hook for this rel
			// enforces one-type-per-column (purges every other type row for
			// the column, then creates or restores the target pair). A
			// client-side delete of oldTypeUUID would race pending creates
			// and can be stale — DEV-11739-QA.
			await API.createMapRecord('listing_column', listingColumnUUID, 'listing_column_type', {
				listing_column_type_uuid: newTypeUUID,
			});
		}, onListingConfigSettled);
	}, [ app, onListingConfigSettled, ]);

	// Builds a linear chain of listing_configs under (rootParentM1,
	// rootParentM1UUID): tiers[0] roots there, each tiers[i] is a child of
	// tiers[i-1]. Each tier shape: { fields?, columns?, settings? }. Returns
	// the root tier's new UUID for caller scroll-into-view. Used by the
	// make / import / integrate flows in ListingConfig.js.
	const createListingConfigTree = useCallback((rootParentM1, rootParentM1UUID, tiers) => {
		if (!tiers || tiers.length === 0) {
			return null;
		}

		const planned = [];
		for (let i = 0; i < tiers.length; i++) {
			const tier = tiers[i] || {};
			const tierUUID = uuidv4();
			const parentM1 = i === 0 ? rootParentM1 : 'listing_config';
			const parentM1UUID = i === 0 ? rootParentM1UUID : planned[i - 1].uuid;
			const rawColumns = Array.isArray(tier.columns) ? tier.columns : [];
			const columnCount = rawColumns.length;
			const evenWidth = columnCount > 0 ? Math.floor(100 / columnCount) : 0;
			const columns = rawColumns.map((col, colIdx) => {
				return {
					uuid:                     uuidv4(),
					display_name:             col.display_name || '',
					listing_column_type_uuid: col.listing_column_type_uuid,
					order:                    colIdx + 1,
					width:                    evenWidth,
				};
			});
			planned.push({
				uuid:         tierUUID,
				parentM1,
				parentM1UUID,
				fields:       tier.fields || {},
				columns,
				settings:     tier.settings || {},
			});
		}

		setListingConfigMap((prev) => {
			const next = {
				...(prev || {}),
			};
			for (let i = 0; i < planned.length; i++) {
				const p = planned[i];
				const entry = {
					...buildInitialListingConfigSettings(),
					...p.fields,
					parentUUID: p.parentM1UUID,
					root:       p.parentM1 === 'account',
					pending:    true,
				};
				if (i < planned.length - 1) {
					entry.childTiers = [ planned[i + 1].uuid, ];
				}
				for (const col of p.columns) {
					entry[`listing_column.${col.uuid}`] = {
						display_name:                                          col.display_name,
						order:                                                 col.order,
						width:                                                 col.width,
						[`listing_column_type.${col.listing_column_type_uuid}`]: {
							uuid: col.listing_column_type_uuid,
						},
						// Same marker addListingColumn uses — downstream
						// (AI enablement, autocomplete options) filters on
						// it so features that require a server-persisted
						// column stay disabled until the create flushes.
						pending: true,
					};
				}
				for (const [ settingUUID, valueObj, ] of Object.entries(p.settings)) {
					entry[`setting.${settingUUID}`] = valueObj;
				}
				next[p.uuid] = entry;
			}
			// account-rooted trees don't have a parent entry in listingConfigMap
			// to wire childTiers onto.
			if (rootParentM1 === 'listing_config' && next[rootParentM1UUID]) {
				next[rootParentM1UUID] = {
					...next[rootParentM1UUID],
					childTiers: [ ...(next[rootParentM1UUID].childTiers || []), planned[0].uuid, ],
				};
			}
			return next;
		});

		// Only account-rooted trees add a new root; listing_config-rooted
		// trees are just child tiers grafted onto an existing parent.
		if (rootParentM1 === 'account') {
			setRootOrder((prev) => {
				return [ ...prev, planned[0].uuid, ];
			});
		}

		// One thunk per tree, keyed by the root UUID so
		// deleteListingConfigTier can drop the create cleanly if the root
		// is removed before Save. Tiers iterate in linked-list order
		// internally so each parent's row exists server-side before the
		// child's parent rel.
		app.queuePendingPromiseGroup('listingConfig', `createListingConfigTree_${planned[0].uuid}`, async () => {
			for (const p of planned) {
				await API.createRecord('listing_config', {
					uuid: p.uuid,
					...p.fields,
				});
				if (p.parentM1 === 'account') {
					await API.createMapRecord('account', p.parentM1UUID, 'listing_config', {
						listing_config_uuid: p.uuid,
					});
				} else {
					// Self-referential map: child goes under alias_uuid, not
					// listing_config_uuid (api_map.go uses alias_uuid when m1==m2).
					await API.createMapRecord('listing_config', p.parentM1UUID, 'listing_config', {
						alias_uuid: p.uuid,
					});
				}
				for (const col of p.columns) {
					await API.createRecord('listing_column', {
						uuid:         col.uuid,
						display_name: col.display_name,
					});
					await API.createMapRecord('listing_column', col.uuid, 'listing_column_type', {
						listing_column_type_uuid: col.listing_column_type_uuid,
					});
					await API.createMapRecord('listing_config', p.uuid, 'listing_column', {
						listing_column_uuid: col.uuid,
						order:               col.order,
						width:               col.width,
					});
				}
				for (const [ settingUUID, valueObj, ] of Object.entries(p.settings)) {
					await API.createMapRecord('listing_config', p.uuid, 'setting', {
						setting_uuid: settingUUID,
						...valueObj,
					});
				}
			}
		}, onListingConfigSettled);

		return planned[0].uuid;
	}, [ app, onListingConfigSettled, ]);

	// Appends a new column under listingConfigUUID, reallocating widths
	// across the existing + new set.
	const addListingColumn = useCallback((listingConfigUUID, options = {}) => {
		const { listingColumnTypeUUID, } = options;
		if (!listingColumnTypeUUID) {
			console.error('addListingColumn requires listingColumnTypeUUID');
			return null;
		}
		const newListingColumnUUID = uuidv4();
		const configEntry = listingConfigMap?.[listingConfigUUID] || {};

		const existingColumns = [];
		for (const [ m2Key, m2Data, ] of Object.entries(configEntry)) {
			if (m2Key.startsWith('listing_column.') && m2Data && m2Data.width !== 0) {
				existingColumns.push({
					uuid:  m2Key.split('.')[1],
					order: m2Data.order ?? 0,
					width: m2Data.width ?? 0,
				});
			}
		}
		existingColumns.sort((a, b) => {
			return a.order - b.order;
		});

		const newOrder = (existingColumns[existingColumns.length - 1]?.order || 0) + 1;
		const hasParent = !!listingConfigMap?.[configEntry.parentUUID];
		const allColumns = reallocateColumnWidths([
			...existingColumns,
			{
				uuid:  newListingColumnUUID,
				order: newOrder,
				width: 1,
			},
		], hasParent);
		const newColData = allColumns[allColumns.length - 1];

		setListingConfigMap((prev) => {
			const next = {
				...(prev || {}),
			};
			const entry = {
				...(next[listingConfigUUID] || {}),
			};
			entry[`listing_column.${newListingColumnUUID}`] = {
				display_name:                                     '',
				order:                                            newOrder,
				width:                                            newColData.width,
				// UUID-keyed placeholder; the trailing refetch replaces it
				// with the canonical name-keyed sub-key + full type metadata.
				[`listing_column_type.${listingColumnTypeUUID}`]: {
					uuid: listingColumnTypeUUID,
				},
				// Drops on the next fetch. Consumers that should hide pending
				// columns (autocomplete, AI toggle) filter on this.
				pending: true,
			};
			for (const c of allColumns) {
				if (c.uuid === newListingColumnUUID) {
					continue;
				}
				entry[`listing_column.${c.uuid}`] = {
					...entry[`listing_column.${c.uuid}`],
					width: c.width,
				};
			}
			next[listingConfigUUID] = entry;
			return next;
		});

		app.queuePendingPromiseGroup('listingConfig', `addListingColumn_${newListingColumnUUID}`, async () => {
			await API.createRecord('listing_column', {
				uuid:         newListingColumnUUID,
				display_name: '',
			});
			await API.createMapRecord('listing_column', newListingColumnUUID, 'listing_column_type', {
				listing_column_type_uuid: listingColumnTypeUUID,
			});
			await API.createMapRecord('listing_config', listingConfigUUID, 'listing_column', {
				listing_column_uuid: newListingColumnUUID,
				order:               newOrder,
				width:               newColData.width,
			});
			for (const c of allColumns) {
				if (c.uuid === newListingColumnUUID) {
					continue;
				}
				await API.updateMapRecord('listing_config', listingConfigUUID, 'listing_column', c.uuid, {
					width: c.width,
				});
			}
		}, onListingConfigSettled);

		return newListingColumnUUID;
	}, [ app, listingConfigMap, onListingConfigSettled, ]);

	// Removes a column and reflows survivors to contiguous 1..n orders +
	// reallocated widths (Data 1 / Data 2 / ..., no gaps). A pending column
	// (added in this same unsaved batch) skips the DELETE — its create is
	// still in the action queue and we can't easily un-queue from inside
	// the buffered group, so the queue runs and then the trailing refetch
	// reconciles. TODO: short-circuit the create when we know we're about
	// to delete.
	const deleteListingColumn = useCallback((listingConfigUUID, listingColumnUUID) => {
		const configEntry = listingConfigMap?.[listingConfigUUID] || {};
		const columnWasPending = !!configEntry[`listing_column.${listingColumnUUID}`]?.pending;

		const survivors = [];
		for (const [ m2Key, m2Data, ] of Object.entries(configEntry)) {
			if (m2Key.startsWith('listing_column.') && m2Data && m2Key !== `listing_column.${listingColumnUUID}` && m2Data.width !== 0) {
				survivors.push({
					uuid:  m2Key.split('.')[1],
					order: m2Data.order ?? 0,
					width: m2Data.width ?? 0,
				});
			}
		}
		survivors.sort((a, b) => {
			return a.order - b.order;
		});

		const hasParent = !!listingConfigMap?.[configEntry.parentUUID];
		const widthAllocated = reallocateColumnWidths(survivors, hasParent);

		setListingConfigMap((prev) => {
			const next = {
				...(prev || {}),
			};
			let entry = {
				...(next[listingConfigUUID] || {}),
			};
			delete entry[`listing_column.${listingColumnUUID}`];
			for (const c of widthAllocated) {
				entry[`listing_column.${c.uuid}`] = {
					...entry[`listing_column.${c.uuid}`],
					width: c.width,
				};
			}
			entry = reorderListingColumns(entry, widthAllocated.map((c) => {
				return c.uuid;
			}));
			next[listingConfigUUID] = entry;
			return next;
		});

		if (columnWasPending) {
			// Added + deleted in the same unsaved batch: drop the queued
			// addListingColumn so neither create nor delete reaches the
			// server. Also drop any follow-up mutations that landed on the
			// pending column between the add and this delete (display_name
			// via updateListingColumn, type via updateListingColumnType);
			// their create was cancelled, so they'd 404 on Save. UUIDs
			// contain no underscores, so splitting on '_' safely tokenises
			// every queue-key shape that mentions this column. The reflow
			// updates on survivors still need to run (we widened them).
			app.cancelPendingPromiseGroupKeysMatching('listingConfig', (key) => {
				return key.split('_').includes(listingColumnUUID);
			});
		}

		app.queuePendingPromiseGroup('listingConfig', `deleteListingColumn_${listingColumnUUID}`, async () => {
			if (!columnWasPending) {
				try {
					await API.deleteMapRecord('listing_config', listingConfigUUID, 'listing_column', listingColumnUUID);
					await API.deleteRecord('listing_column', listingColumnUUID);
				} catch (err) {
					console.error('deleteListingColumn (remove) failed:', err);
				}
			}
			for (let i = 0; i < widthAllocated.length; i++) {
				const c = widthAllocated[i];
				try {
					await API.updateMapRecord('listing_config', listingConfigUUID, 'listing_column', c.uuid, {
						order: i + 1,
						width: c.width,
					});
				} catch (err) {
					console.error('deleteListingColumn (reflow) failed:', err);
				}
			}
		}, onListingConfigSettled);
	}, [ app, listingConfigMap, onListingConfigSettled, ]);

	useImperativeHandle(ref, () => {
		return {
			getListingConfigMap: () => {
				return listingConfigMap;
			},
			fetchListingConfigs,
			clearListingConfigMap,
			applyListingConfigCredentials,
			updateListingConfig,
			updateListingConfigLabel,
			deleteListingConfigTier,
			dropListingConfigSubtreeLocally,
			createListingConfigTree,
			addListingColumn,
			deleteListingColumn,
			updateListingColumn,
			updateListingColumnType,
		};
	}, [ listingConfigMap, fetchListingConfigs, clearListingConfigMap, applyListingConfigCredentials, updateListingConfig, updateListingConfigLabel, deleteListingConfigTier, dropListingConfigSubtreeLocally, createListingConfigTree, addListingColumn, deleteListingColumn, updateListingColumn, updateListingColumnType, ]);

	const value = useMemo(() => {
		return {
			listingConfigMap,
			listingConfigMapLoaded,
			rootOrder,
			fetchListingConfigs,
			clearListingConfigMap,
			applyListingConfigCredentials,
			updateListingConfig,
			updateListingConfigLabel,
			deleteListingConfigTier,
			dropListingConfigSubtreeLocally,
			createListingConfigTree,
			addListingColumn,
			deleteListingColumn,
			updateListingColumn,
			updateListingColumnType,
		};
	}, [ listingConfigMap, listingConfigMapLoaded, rootOrder, fetchListingConfigs, clearListingConfigMap, applyListingConfigCredentials, updateListingConfig, updateListingConfigLabel, deleteListingConfigTier, dropListingConfigSubtreeLocally, createListingConfigTree, addListingColumn, deleteListingColumn, updateListingColumn, updateListingColumnType, ]);

	return (
		<ListingConfigContext.Provider value={value}>
			{children}
		</ListingConfigContext.Provider>
	);
});
